import React, {useContext, createContext, useEffect, useRef} from 'react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Heading,
    Image,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'

const ToggleCardContext = createContext()

export const ToggleCard = ({
    id,
    title,
    editing,
    disabled,
    disableEdit,
    onEdit,
    editLabel,
    isLoading,
    children,
    step,
    displayPreIcons = true,
    titleStyles,
    ...props
}) => {
    const titleRef = useRef()
    useEffect(() => {
        if (editing && titleRef.current) {
            titleRef.current.focus()
        }
    }, [editing])

    return (
        <ToggleCardContext.Provider value={{editing, disabled}}>
            <Box
                layerStyle="card"
                marginTop={[
                    `${pxToRem(20)} !important`,
                    `${pxToRem(20)} !important`,
                    `${pxToRem(25)} !important`,
                    `${pxToRem(25)} !important`
                ]}
                data-testid={`sf-toggle-card-${id}`}
                position="relative"
                boxShadow="2px 4px 16.6px 2px rgba(0, 0, 0, 0.25)"
                borderRadius={0}
                {...props}
            >
                <Stack spacing={editing || (!editing && !disabled) ? 4 : 0}>
                    <Flex justify="space-between">
                        <Box
                            display={'flex'}
                            gap={pxToRem(15)}
                            marginBottom={{base: pxToRem(15), lg: pxToRem(30)}}
                        >
                            {displayPreIcons &&
                                (!editing && !disabled ? (
                                    <Image
                                        src={getAssetUrl('static/svg/correct_red.svg')}
                                        alt="step 2"
                                        marginBottom={pxToRem(8)}
                                    />
                                ) : (
                                    <Box
                                        height={pxToRem(25)}
                                        width={pxToRem(25)}
                                        borderWidth={pxToRem(1)}
                                        borderColor={'cav_calltoaction_darkred'}
                                        backgroundColor={
                                            editing ? 'cav_calltoaction_darkred' : 'white'
                                        }
                                        display={'flex'}
                                        justifyContent={'center'}
                                        alignItems={'center'}
                                        color={editing ? 'white' : 'cav_calltoaction_darkred'}
                                        fontWeight={700}
                                        borderRadius={'50%'}
                                        fontSize={pxToRem(16)}
                                        marginTop={pxToRem(4)}
                                    >
                                        {step}
                                    </Box>
                                ))}
                            <Heading
                                fontSize={{base: pxToRem(16), lg: pxToRem(22)}}
                                lineHeight="normal"
                                letterSpacing={{base: pxToRem(2), lg: pxToRem(3)}}
                                textTransform={'uppercase'}
                                color={'black'}
                                ref={titleRef}
                                {...titleStyles}
                                fontFamily={fonts.primary}
                                marginBottom={0}
                            >
                                {title}
                            </Heading>
                        </Box>
                    </Flex>
                    <Box
                        data-testid={`sf-toggle-card-${id}-content`}
                        fontFamily={fonts.primary}
                        marginTop={'0 !important'}
                        spacing={0}
                    >
                        {children}
                    </Box>
                    {!editing && !disabled && onEdit && !disableEdit && (
                        <Button
                            variant="link"
                            onClick={onEdit}
                            aria-label={editLabel}
                            position={'absolute'}
                            bottom={pxToRem(18)}
                            right={pxToRem(18)}
                            fontWeight={500}
                            color="cav_red"
                            fontSize={{base: '10px', lg: '12px'}}
                            textDecoration={'none'}
                            textTransform="uppercase"
                            letterSpacing={pxToRem(1)}
                        >
                            {editLabel || (
                                <FormattedMessage
                                    defaultMessage="Edit"
                                    id="toggle_card.action.edit"
                                />
                            )}
                        </Button>
                    )}
                </Stack>

                {isLoading && editing && <LoadingSpinner wrapperStyles={{zIndex: 1}} />}
            </Box>
        </ToggleCardContext.Provider>
    )
}

export const ToggleCardEdit = ({children}) => {
    const {editing} = useContext(ToggleCardContext)
    return editing ? children : null
}

export const ToggleCardSummary = ({children}) => {
    const {editing, disabled} = useContext(ToggleCardContext)
    return !editing && !disabled ? children : null
}

ToggleCard.propTypes = {
    id: PropTypes.string,
    title: PropTypes.any,
    editLabel: PropTypes.any,
    editing: PropTypes.bool,
    isLoading: PropTypes.bool,
    disabled: PropTypes.bool,
    disableEdit: PropTypes.bool,
    onEdit: PropTypes.func,
    children: PropTypes.any,
    step: PropTypes.number,
    displayPreIcons: PropTypes.bool,
    titleStyles: PropTypes.object
}

ToggleCardEdit.propTypes = {
    children: PropTypes.any
}

ToggleCardSummary.propTypes = {
    children: PropTypes.any
}
